import type {PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {INFINITY_THRESHOLD} from "@/store/constants";
import type {TPackageName} from "@/store/types";
import dayjs from "dayjs";

export type TPlanFeatureKey =
  | "access_to_ad_codes"
  | "access_to_influencer_network"
  | "account_setup_call"
  | "buyout"
  | "content_approval"
  | "customer_success"
  | "influencers_per_campaign"
  | "invitations_per_campaign"
  | "number_of_multiple_active_campaigns"
  | "pro_creator_mode"
  | "social_feed_filters"
  | "tiktok_visibility_country_count"
  | "tiktok_visibility_keyword_limit"
  | "tiktok_visibility_sync_interval_hours";

export type TPlanFeature = {
  key: TPlanFeatureKey;
  value: number | boolean | null | undefined;
};

const KEYS_WITH_SPECIAL_TRANSLATIONS: TPlanFeatureKey[] = [
  "invitations_per_campaign",
  "number_of_multiple_active_campaigns",
  "tiktok_visibility_country_count",
  "tiktok_visibility_keyword_limit",
  "tiktok_visibility_sync_interval_hours",
];

export const TIKTOK_KEYWORD_LIMIT_INFINITY = 200;

export const TIKTOK_FREQUENCY_BY_HOURS: Record<
  number,
  {
    sentence: string;
    word: string;
  }
> = {
  24: {sentence: "tiktok_visibility_sync_interval_hours_daily", word: "tiktok_visibility_frequency_daily"},
  168: {sentence: "tiktok_visibility_sync_interval_hours_weekly", word: "tiktok_visibility_frequency_weekly"},
  720: {sentence: "tiktok_visibility_sync_interval_hours_monthly", word: "tiktok_visibility_frequency_monthly"},
};

const ALWAYS_TRUE_KEYS: TPlanFeatureKey[] = [
  "access_to_ad_codes",
  "tiktok_visibility_country_count",
  "tiktok_visibility_keyword_limit",
  "tiktok_visibility_sync_interval_hours",
];

const PLAN_PRIORITY = {
  ADVANCED: 1,
  ENTERPRISE: 0,
  ESSENTIALS: 0,
  EXPERT: 3,
  NONE: 0,
  PROFESSIONAL: 2,
  STARTER: 0,
};

const PREFIXED_WITH_VALUE: TPlanFeatureKey[] = [
  "buyout",
  "influencers_per_campaign",
  "tiktok_visibility_keyword_limit",
];

export type TChipType = "above_power" | "power" | "recommended" | "unsupported";

const getSpecialTranslation = (feature: TPlanFeature) => {
  const {key, value} = feature;

  if (key === "number_of_multiple_active_campaigns") {
    const type = value === null ? "_unlimited" : value === 1 ? "" : "_value";

    return "number_of_multiple_active_campaigns" + type;
  }

  if (key === "invitations_per_campaign") {
    return value && +value >= INFINITY_THRESHOLD
      ? key
      : value && +value > 0
        ? "invitations_per_campaign_value"
        : "invitations_per_campaign_empty";
  }

  if (key === "tiktok_visibility_country_count" && typeof value === "number") {
    return "tiktok_search_countries";
  }

  if (key === "tiktok_visibility_keyword_limit" && typeof value === "number") {
    return isInfinite(value, key) ? "keywords" : "tiktok_visibility_keyword_limit";
  }

  if (key === "tiktok_visibility_sync_interval_hours" && typeof value === "number") {
    return TIKTOK_FREQUENCY_BY_HOURS[value].sentence;
  }

  return "";
};

const isSpecialTrueCase = (feature: TPlanFeature) => {
  return feature.key === "number_of_multiple_active_campaigns" && feature.value === null;
};

const isSpecialFalseCase = (feature: TPlanFeature) => {
  return feature.key === "number_of_multiple_active_campaigns" && feature.value === 1;
};

export const isFeatureOn = (feature: TPlanFeature) => {
  const {key, value} = feature;

  if (ALWAYS_TRUE_KEYS.includes(key)) return true;
  if (isSpecialTrueCase(feature)) return true;
  if (isSpecialFalseCase(feature)) return false;

  return !!value;
};

export const getFeatureTranslationKey = (feature: TPlanFeature) => {
  return KEYS_WITH_SPECIAL_TRANSLATIONS.includes(feature.key) ? getSpecialTranslation(feature) : feature.key;
};

const FEATURE_INFINITE_THRESHOLDS: Partial<Record<TPlanFeatureKey, number>> = {
  tiktok_visibility_keyword_limit: 200,
};

export const isInfinite = (value: number | boolean | null | undefined, key?: TPlanFeatureKey) => {
  if (typeof value !== "number") return false;
  return value >= (key ? (FEATURE_INFINITE_THRESHOLDS[key] ?? INFINITY_THRESHOLD) : INFINITY_THRESHOLD);
};
export const isPrefixedWithValue = (feature: TPlanFeature) =>
  typeof feature.value === "number" &&
  !isInfinite(feature.value, feature.key) &&
  PREFIXED_WITH_VALUE.includes(feature.key);

export const getRecommendationLevel = ({
  packageType,
  recommendedPackageType,
}: {
  packageType: TPackageName;
  recommendedPackageType: TPackageName;
}) => {
  const packageRank = PLAN_PRIORITY[packageType];
  const recommendedPackageRank = PLAN_PRIORITY[recommendedPackageType];

  return packageRank === recommendedPackageRank
    ? "recommended"
    : packageRank < recommendedPackageRank
      ? "unsupported"
      : packageRank === recommendedPackageRank + 1
        ? "power"
        : "above_power";
};

// A free trial an admin assigned by hand: no Stripe subscription behind it, so it never reports
// the "trialing" status and has to be recognised by its own flag while it is still running.
export const isActiveAdminAssignedTrial = (
  subscription?: Pick<PackageSubscriptionSerializer, "admin_assigned_trial" | "valid_until"> | null,
) => !!subscription?.admin_assigned_trial && dayjs(subscription.valid_until).isAfter(dayjs());

export const getAdminAssignedTrialDaysLeft = (
  subscription?: Pick<PackageSubscriptionSerializer, "valid_until"> | null,
) => Math.max(dayjs(subscription?.valid_until).diff(dayjs(), "day"), 0);
